import { useState } from 'react';
import { clsx } from 'clsx';
import { AlertTriangle } from 'lucide-react';
import { SectionAccordion } from './SectionAccordion';
import { CriterionInput } from './CriterionInput';
import { ScorePreview } from './ScorePreview';
import type {
  PublicFramework,
  PredefinedRedFlag,
  Evaluation,
  SaveEvaluationRequest,
} from '../../types';

interface FormScores {
  [criterionId: string]: {
    score: number | null;
    rationale: string;
  };
}

interface EvaluationFormProps {
  framework: PublicFramework;
  redFlagDefinitions: PredefinedRedFlag[];
  existingEvaluation?: Evaluation;
  isSubmitting: boolean;
  onSubmit: (data: SaveEvaluationRequest) => void;
  onCancel?: () => void;
}

export function EvaluationForm({
  framework,
  redFlagDefinitions,
  existingEvaluation,
  isSubmitting,
  onSubmit,
}: EvaluationFormProps) {
  // Initialize scores from existing evaluation or empty
  const [scores, setScores] = useState<FormScores>(() => {
    if (existingEvaluation) {
      const initial: FormScores = {};
      for (const [id, value] of Object.entries(existingEvaluation.scores)) {
        initial[id] = { score: value.score, rationale: value.rationale };
      }
      return initial;
    }
    // Initialize empty scores for all criteria
    const initial: FormScores = {};
    for (const section of framework.sections) {
      for (const criterion of section.criteria) {
        initial[criterion.id] = { score: null, rationale: '' };
      }
    }
    return initial;
  });

  const [redFlags, setRedFlags] = useState<string[]>(() => {
    if (existingEvaluation) {
      // Extract just the IDs from the red flags
      return existingEvaluation.red_flags.map((f) =>
        typeof f === 'string' ? f : f.id
      );
    }
    return [];
  });

  const [notes, setNotes] = useState<string>(
    existingEvaluation?.summary?.recommendation || ''
  );

  const [expandedSection, setExpandedSection] = useState<string | null>(() => {
    // Auto-expand first incomplete section
    for (const section of framework.sections) {
      for (const criterion of section.criteria) {
        const entry = scores[criterion.id];
        if (!entry || entry.score === null || !entry.rationale) {
          return section.id;
        }
      }
    }
    return framework.sections[0]?.id || null;
  });

  const [validationErrors, setValidationErrors] = useState<string[]>([]);

  const handleScoreChange = (criterionId: string, score: number) => {
    setScores((prev) => ({
      ...prev,
      [criterionId]: { ...prev[criterionId], score },
    }));
    setValidationErrors([]);
  };

  const handleRationaleChange = (criterionId: string, rationale: string) => {
    setScores((prev) => ({
      ...prev,
      [criterionId]: { ...prev[criterionId], rationale },
    }));
    setValidationErrors([]);
  };

  const handleRedFlagToggle = (flagId: string) => {
    setRedFlags((prev) =>
      prev.includes(flagId)
        ? prev.filter((id) => id !== flagId)
        : [...prev, flagId]
    );
  };

  const getSectionProgress = (sectionId: string) => {
    const section = framework.sections.find((s) => s.id === sectionId);
    if (!section) return { completed: 0, total: 0 };

    let completed = 0;
    for (const criterion of section.criteria) {
      const entry = scores[criterion.id];
      if (
        entry?.score !== null &&
        entry?.rationale?.length >= 1 &&
        entry?.rationale?.length <= 500
      ) {
        completed++;
      }
    }
    return { completed, total: section.criteria.length };
  };

  const validateForm = (): string[] => {
    const errors: string[] = [];
    for (const section of framework.sections) {
      for (const criterion of section.criteria) {
        const entry = scores[criterion.id];
        if (!entry || entry.score === null) {
          errors.push(`${criterion.id}: Score required`);
        }
        if (!entry || !entry.rationale || entry.rationale.length < 1) {
          errors.push(`${criterion.id}: Rationale required`);
        }
        if (entry && entry.rationale && entry.rationale.length > 500) {
          errors.push(`${criterion.id}: Rationale exceeds 500 characters`);
        }
      }
    }
    if (notes && notes.length > 2000) {
      errors.push('Notes exceed 2000 characters');
    }
    return errors;
  };

  const handleSubmit = () => {
    const errors = validateForm();
    if (errors.length > 0) {
      setValidationErrors(errors);
      // Expand section with first error
      const firstError = errors[0];
      const criterionId = firstError.split(':')[0];
      for (const section of framework.sections) {
        if (section.criteria.some((c) => c.id === criterionId)) {
          setExpandedSection(section.id);
          break;
        }
      }
      return;
    }

    // Build request
    const request: SaveEvaluationRequest = {
      scores: Object.fromEntries(
        Object.entries(scores)
          .filter(([, v]) => v.score !== null)
          .map(([k, v]) => [k, { score: v.score!, rationale: v.rationale }])
      ),
      red_flags: redFlags,
      notes: notes || undefined,
    };

    onSubmit(request);
  };

  return (
    <div className="space-y-6 pb-20">
      {/* Edit warning banner */}
      {existingEvaluation && (
        <div className="bg-amber-500/10 border border-amber-500/30 p-4 rounded-lg flex items-center gap-3">
          <AlertTriangle className="w-5 h-5 text-amber-400 flex-shrink-0" />
          <p className="text-amber-200 text-sm">
            Submitting will replace your previous evaluation.
          </p>
        </div>
      )}

      {/* Validation errors */}
      {validationErrors.length > 0 && (
        <div className="bg-red-500/10 border border-red-500/30 p-4 rounded-lg">
          <p className="text-red-400 font-medium mb-2">
            Please fix the following errors:
          </p>
          <ul className="text-red-300 text-sm list-disc list-inside">
            {validationErrors.slice(0, 5).map((error, i) => (
              <li key={i}>{error}</li>
            ))}
            {validationErrors.length > 5 && (
              <li>...and {validationErrors.length - 5} more</li>
            )}
          </ul>
        </div>
      )}

      {/* Scoring sections */}
      {framework.sections.map((section) => {
        const progress = getSectionProgress(section.id);
        return (
          <SectionAccordion
            key={section.id}
            sectionId={section.id}
            name={section.name}
            weight={section.weight}
            completedCount={progress.completed}
            totalCount={progress.total}
            isExpanded={expandedSection === section.id}
            onToggle={() =>
              setExpandedSection(
                expandedSection === section.id ? null : section.id
              )
            }
          >
            {section.criteria.map((criterion) => (
              <CriterionInput
                key={criterion.id}
                criterionId={criterion.id}
                name={criterion.name}
                question={criterion.question}
                score={scores[criterion.id]?.score ?? null}
                rationale={scores[criterion.id]?.rationale ?? ''}
                onScoreChange={(score) => handleScoreChange(criterion.id, score)}
                onRationaleChange={(rationale) =>
                  handleRationaleChange(criterion.id, rationale)
                }
              />
            ))}
          </SectionAccordion>
        );
      })}

      {/* Red Flags Section */}
      <div className="border border-wolf-700/50 rounded-lg p-4">
        <h3 className="text-wolf-100 font-medium mb-4 flex items-center gap-2">
          <AlertTriangle className="w-5 h-5 text-red-400" />
          Red Flags (Optional)
        </h3>
        <p className="text-wolf-400 text-sm mb-4">
          Check any concerns that apply. Flagging any item will result in a
          BLOCKED decision.
        </p>
        <div className="space-y-3">
          {redFlagDefinitions.map((flag) => (
            <label
              key={flag.id}
              className={clsx(
                'flex items-start gap-3 p-3 rounded-lg cursor-pointer transition-colors',
                'border',
                redFlags.includes(flag.id)
                  ? 'border-red-500/50 bg-red-500/10'
                  : 'border-wolf-700/50 bg-wolf-900/30 hover:bg-wolf-800/30'
              )}
            >
              <input
                type="checkbox"
                checked={redFlags.includes(flag.id)}
                onChange={() => handleRedFlagToggle(flag.id)}
                className="mt-1 accent-red-500"
              />
              <div>
                <span className="text-wolf-100 font-medium">{flag.label}</span>
                <p className="text-wolf-400 text-sm mt-1">{flag.description}</p>
              </div>
            </label>
          ))}
        </div>
      </div>

      {/* Notes Section */}
      <div className="border border-wolf-700/50 rounded-lg p-4">
        <h3 className="text-wolf-100 font-medium mb-3">
          Reviewer Notes (Optional)
        </h3>
        <textarea
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          placeholder="Additional comments, recommendations, or concerns..."
          rows={4}
          className={clsx(
            'w-full px-3 py-2 rounded-lg bg-wolf-900/50 border text-wolf-100',
            'placeholder-wolf-500 resize-none',
            'focus:outline-none focus:ring-2 focus:ring-action/50',
            notes.length > 2000 ? 'border-red-500' : 'border-wolf-700/50'
          )}
        />
        <div className="flex justify-end mt-1">
          <span
            className={clsx(
              'text-xs',
              notes.length > 2000 ? 'text-red-400' : 'text-wolf-500'
            )}
          >
            {notes.length}/2000
          </span>
        </div>
      </div>

      {/* Sticky Score Preview */}
      <ScorePreview
        scores={scores}
        redFlags={redFlags}
        framework={framework}
        isSubmitting={isSubmitting}
        onSubmit={handleSubmit}
      />
    </div>
  );
}
